<script setup lang="ts">
  import Button from '@/components/ui/button/Button.vue'
  import DropdownMenu from '@/components/ui/dropdown-menu/DropdownMenu.vue'
  import DropdownMenuContent from '@/components/ui/dropdown-menu/DropdownMenuContent.vue'
  import DropdownMenuItem from '@/components/ui/dropdown-menu/DropdownMenuItem.vue'
  import DropdownMenuTrigger from '@/components/ui/dropdown-menu/DropdownMenuTrigger.vue'
  import { useWindowSize } from '@/composables/useWindowSize'
  import { Execution } from '@/shared/Executions/models/Execution'
  import { ExecutionStatusType } from '@/shared/Executions/models/ExecutionStatusType'
  import {
    Archive,
    BookText,
    CalculatorIcon,
    CircleX,
    Download,
    EllipsisIcon,
    FileDown,
    FileCog,
    FolderOutput,
    Loader2,
    RotateCw,
    FileJson
  } from 'lucide-vue-next'
  import { HierarchicExecutionViewModel } from '../models/HierarchicExecutionViewModel'
  import { computed, toRef } from 'vue'
  import {
    AdvancedAnalysisPhaseType,
    ExecutionPhaseType
  } from '@/shared/Executions/models/ExecutionPhaseType'
  import { IExecutionPhaseService } from '@/shared/Executions/services/ExecutionPhaseService'
  import { ExecutionHistoryReloadActionType } from '../models/ExecutionActionReloadActionType'

  interface IExecutionActionsColumnProps {
    record: HierarchicExecutionViewModel
    index?: number
    selectable?: boolean
    downloadingExecutionId?: string
    downloadingReportExecutionId?: string
    cancelingExecutionIds?: Set<string>
    archivingExecutionIds?: Set<string>
    onCellClick?: (record: Execution, e?: Event) => void
    executionPhaseService: IExecutionPhaseService
    hasAdminAccess?: boolean
    hideReloadActions?: boolean
    showDownloadReport?: boolean
    onMoveToFolderClicked?: (record: HierarchicExecutionViewModel) => void
  }

  const { isMediumTierOrLess } = useWindowSize()

  const props = defineProps<IExecutionActionsColumnProps>()
  const record = toRef(props, 'record')

  const emit = defineEmits<{
    'reload-clicked': [
      execution: HierarchicExecutionViewModel,
      actionType: ExecutionHistoryReloadActionType
    ]
    'reload-advanced-analysis-clicked': [execution: HierarchicExecutionViewModel]
    'download-clicked': [execution: HierarchicExecutionViewModel]
    'download-report-clicked': [execution: HierarchicExecutionViewModel]
    'custom-calculations-clicked': [
      execution: HierarchicExecutionViewModel,
      actionType: ExecutionHistoryReloadActionType
    ]
    'post-processing-clicked': [
      execution: HierarchicExecutionViewModel,
      actionType: ExecutionHistoryReloadActionType
    ]
    'custom-report-clicked': [
      execution: HierarchicExecutionViewModel,
      actionType: ExecutionHistoryReloadActionType
    ]
    'cancel-clicked': [execution: HierarchicExecutionViewModel]
    'archive-clicked': [execution: HierarchicExecutionViewModel]
    'move-to-folder-clicked': [execution: HierarchicExecutionViewModel]
  }>()

  const shouldShowReloadAction = computed(
    () =>
      !props.hideReloadActions &&
      record.value.primaryExecutionPhase === ExecutionPhaseType.NCAAnalysis
  )

  const shouldShowCalculationsAction = computed(
    () =>
      !props.hideReloadActions &&
      record.value.primaryExecutionPhase === ExecutionPhaseType.CustomCalculations
  )

  const shouldShowPostProcessingAction = computed(
    () =>
      !props.hideReloadActions &&
      record.value.primaryExecutionPhase === ExecutionPhaseType.Visualization
  )

  const shouldShowCustomReportsAction = computed(
    () =>
      !props.hideReloadActions && record.value.primaryExecutionPhase === ExecutionPhaseType.Reports
  )

  const shouldShowAdvancedAnalysisAction = computed(
    () =>
      !props.hideReloadActions && record.value.primaryExecutionPhase === AdvancedAnalysisPhaseType
  )

  const isCancelingExecution = computed(() => {
    return record?.value?.id != null && props.cancelingExecutionIds?.has(record.value.id)
  })

  const isArchivingExecution = computed(() => {
    return record?.value?.id != null && props.archivingExecutionIds?.has(record.value.id)
  })

  const onReloadActionClicked = (
    execution: HierarchicExecutionViewModel,
    actionType: ExecutionHistoryReloadActionType
  ): void => {
    emit('reload-clicked', execution, actionType)
  }

  const onReloadAdvancedAnalysisActionClicked = (execution: HierarchicExecutionViewModel): void => {
    emit('reload-advanced-analysis-clicked', execution)
  }

  const onDownloadActionClicked = (execution: HierarchicExecutionViewModel): void => {
    emit('download-clicked', execution)
  }

  const onDownloadReportActionClicked = (execution: HierarchicExecutionViewModel): void => {
    emit('download-report-clicked', execution)
  }

  const onCustomCalculationActionClicked = (
    execution: HierarchicExecutionViewModel,
    actionType: ExecutionHistoryReloadActionType
  ): void => {
    emit('custom-calculations-clicked', execution, actionType)
  }

  const onPostProcessingActionClicked = (
    execution: HierarchicExecutionViewModel,
    actionType: ExecutionHistoryReloadActionType
  ): void => {
    emit('post-processing-clicked', execution, actionType)
  }

  const onCustomReportActionClicked = (
    execution: HierarchicExecutionViewModel,
    actionType: ExecutionHistoryReloadActionType
  ): void => {
    emit('custom-report-clicked', execution, actionType)
  }

  const onCancelActionClicked = (execution: HierarchicExecutionViewModel): void => {
    emit('cancel-clicked', execution)
  }

  const onArchiveActionClicked = (execution: HierarchicExecutionViewModel): void => {
    emit('archive-clicked', execution)
  }

  const onMoveToFolderClicked = (execution: HierarchicExecutionViewModel): void => {
    if (props.onMoveToFolderClicked) {
      props.onMoveToFolderClicked(execution)
    }
    emit('move-to-folder-clicked', execution)
  }
</script>

<template>
  <!-- On smaller screens render a dropdown menu with the same actions -->
  <div v-if="isMediumTierOrLess" class="flex gap-1">
    <DropdownMenu>
      <DropdownMenuTrigger as-child>
        <Button variant="default" size="icon">
          <EllipsisIcon />
          <span class="sr-only">Actions</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" class="min-w-[200px]">
        <!-- Reload -->
        <DropdownMenuItem
          @click="
            () => onReloadActionClicked(record, ExecutionHistoryReloadActionType.AnalysisWizard)
          "
          v-if="shouldShowReloadAction"
          class="gap-2"
        >
          <RotateCw class="size-4" />
          <span>Reload Analysis</span>
        </DropdownMenuItem>

        <DropdownMenuItem
          @click="
            () =>
              onReloadActionClicked(record, ExecutionHistoryReloadActionType.AdvancedAnalysisWizard)
          "
          v-if="shouldShowReloadAction"
          class="gap-2"
        >
          <FileJson class="size-4" />
          <span>Reload as Advanced Analysis</span>
        </DropdownMenuItem>

        <!-- Reload Advanced Analysis -->
        <DropdownMenuItem
          @click="() => onReloadAdvancedAnalysisActionClicked(record)"
          v-if="shouldShowAdvancedAnalysisAction"
          class="gap-2"
        >
          <RotateCw class="size-4" />
          <span>Reload Advanced Analysis</span>
        </DropdownMenuItem>

        <!-- Custom Calculations -->
        <DropdownMenuItem
          @click="
            () =>
              onCustomCalculationActionClicked(
                record,
                ExecutionHistoryReloadActionType.AnalysisWizard
              )
          "
          v-if="shouldShowCalculationsAction"
          :disabled="record?.status !== ExecutionStatusType.Succeeded"
          class="gap-2"
        >
          <CalculatorIcon class="size-4" />
          <span>Reload Custom Calculations</span>
        </DropdownMenuItem>

        <DropdownMenuItem
          @click="
            () =>
              onCustomCalculationActionClicked(
                record,
                ExecutionHistoryReloadActionType.AdvancedAnalysisWizard
              )
          "
          v-if="shouldShowCalculationsAction"
          :disabled="record?.status !== ExecutionStatusType.Succeeded"
          class="gap-2"
        >
          <FileJson class="size-4" />
          <span>Reload as Advanced Analysis</span>
        </DropdownMenuItem>

        <!-- Post Processing -->
        <DropdownMenuItem
          @click="
            () =>
              onPostProcessingActionClicked(record, ExecutionHistoryReloadActionType.AnalysisWizard)
          "
          :disabled="record?.status !== ExecutionStatusType.Succeeded"
          v-if="shouldShowPostProcessingAction"
          class="gap-2"
        >
          <FileCog class="size-4" />
          <span>Reload Post Processing</span>
        </DropdownMenuItem>

        <DropdownMenuItem
          @click="
            () =>
              onPostProcessingActionClicked(
                record,
                ExecutionHistoryReloadActionType.AdvancedAnalysisWizard
              )
          "
          :disabled="record?.status !== ExecutionStatusType.Succeeded"
          v-if="shouldShowPostProcessingAction"
          class="gap-2"
        >
          <FileJson class="size-4" />
          <span>Reload as Advanced Analysis</span>
        </DropdownMenuItem>

        <!-- Custom Reports -->
        <DropdownMenuItem
          @click="
            () =>
              onCustomReportActionClicked(record, ExecutionHistoryReloadActionType.AnalysisWizard)
          "
          :disabled="record?.status !== ExecutionStatusType.Succeeded"
          v-if="shouldShowCustomReportsAction"
          class="gap-2"
        >
          <BookText class="size-4" />
          <span>Reload Report</span>
        </DropdownMenuItem>

        <DropdownMenuItem
          @click="
            () =>
              onCustomReportActionClicked(
                record,
                ExecutionHistoryReloadActionType.AdvancedAnalysisWizard
              )
          "
          :disabled="record?.status !== ExecutionStatusType.Succeeded"
          v-if="shouldShowCustomReportsAction"
          class="gap-2"
        >
          <BookText class="size-4" />
          <span>Reload as Advanced Analysis</span>
        </DropdownMenuItem>

        <!-- Download Results -->
        <DropdownMenuItem
          @click="() => onDownloadActionClicked(record)"
          v-if="
            record?.status === ExecutionStatusType.Succeeded ||
            record?.status === ExecutionStatusType.Failed
          "
          class="gap-2"
        >
          <Loader2 v-if="downloadingExecutionId === record.id" class="size-4 animate-spin" />
          <Download v-else class="size-4" />
          <span>Download</span>
        </DropdownMenuItem>

        <!-- Download Report -->
        <DropdownMenuItem
          @click="() => onDownloadReportActionClicked(record)"
          v-if="
            showDownloadReport &&
            (record?.status === ExecutionStatusType.Succeeded ||
              record?.status === ExecutionStatusType.Failed)
          "
          class="gap-2"
        >
          <Loader2 v-if="downloadingReportExecutionId === record.id" class="size-4 animate-spin" />
          <FileDown v-else class="size-4" />
          <span>Download Report</span>
        </DropdownMenuItem>

        <!-- Cancel Execution -->
        <DropdownMenuItem
          @click="() => onCancelActionClicked(record)"
          v-if="hasAdminAccess"
          :disabled="
            record?.status !== ExecutionStatusType.Pending &&
            record?.status !== ExecutionStatusType.Running
          "
          class="gap-2"
        >
          <Loader2 v-if="isCancelingExecution" class="size-4 animate-spin" />
          <CircleX v-else class="size-4" />
          <span>Cancel</span>
        </DropdownMenuItem>

        <!-- Archive Execution -->
        <DropdownMenuItem
          @click="() => onArchiveActionClicked(record)"
          v-if="hasAdminAccess"
          :disabled="
            record?.status !== ExecutionStatusType.Succeeded &&
            record?.status !== ExecutionStatusType.Failed &&
            record?.status !== ExecutionStatusType.Cancelled
          "
          class="gap-2"
        >
          <Loader2 v-if="isArchivingExecution" class="size-4 animate-spin" />
          <Archive v-else class="size-4" />
          <span>Archive</span>
        </DropdownMenuItem>

        <!-- Move to Folder (platform_developer only) -->
        <DropdownMenuItem
          v-if="props.hasAdminAccess"
          @click="() => onMoveToFolderClicked(record)"
          class="gap-2"
        >
          <FolderOutput class="size-4" />
          <span>Move to Folder</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  </div>

  <!-- If not small screen, render original button group -->
  <div v-else class="flex gap-1">
    <a-tooltip v-if="shouldShowReloadAction" title="Reload Analysis">
      <span class="inline-block">
        <DropdownMenu>
          <DropdownMenuTrigger as-child>
            <Button
              id="reload"
              role="reload"
              size="icon"
              variant="secondary"
              class="group relative"
            >
              <RotateCw class="size-5" />
              <svg
                class="absolute bottom-0.5 right-0.5 size-2 fill-current opacity-50 transition group-hover:opacity-100"
                viewBox="0 0 9 9"
                aria-hidden="true"
              >
                <polygon points="1,1 7,1 4,6" />
              </svg>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" class="min-w-[200px]">
            <!-- Reload -->
            <DropdownMenuItem
              @click="
                () => onReloadActionClicked(record, ExecutionHistoryReloadActionType.AnalysisWizard)
              "
              class="gap-2"
            >
              <RotateCw class="size-4" />
              <span>Reload Analysis</span>
            </DropdownMenuItem>

            <DropdownMenuItem
              @click="
                () =>
                  onReloadActionClicked(
                    record,
                    ExecutionHistoryReloadActionType.AdvancedAnalysisWizard
                  )
              "
              class="gap-2"
            >
              <FileJson class="size-4" />
              <span>Reload as Advanced Analysis</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </span>
    </a-tooltip>

    <a-tooltip v-if="shouldShowAdvancedAnalysisAction" title="Reload Advanced Analysis">
      <span class="inline-block">
        <Button
          id="reloadAdvancedAnalysis"
          role="reloadAdvancedAnalysis"
          size="icon"
          variant="secondary"
          @click="() => onReloadAdvancedAnalysisActionClicked(record)"
        >
          <RotateCw class="size-5" />
        </Button>
      </span>
    </a-tooltip>

    <a-tooltip v-if="shouldShowCalculationsAction" title="Reload Custom Calculations">
      <span class="inline-block">
        <DropdownMenu>
          <DropdownMenuTrigger as-child>
            <Button
              id="custom_calculation"
              role="custom_calculation"
              size="icon"
              :disabled="record?.status !== ExecutionStatusType.Succeeded"
              variant="secondary"
              class="group relative"
            >
              <CalculatorIcon class="size-5" />
              <svg
                class="absolute bottom-0.5 right-0.5 size-2 fill-current opacity-50 transition group-hover:opacity-100"
                viewBox="0 0 9 9"
                aria-hidden="true"
              >
                <polygon points="1,1 7,1 4,6" />
              </svg>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" class="min-w-[200px]">
            <!-- Reload -->
            <DropdownMenuItem
              @click="
                onCustomCalculationActionClicked(
                  record,
                  ExecutionHistoryReloadActionType.AnalysisWizard
                )
              "
              class="gap-2"
            >
              <RotateCw class="size-4" />
              <span>Reload Calculations</span>
            </DropdownMenuItem>

            <DropdownMenuItem
              @click="
                () =>
                  onCustomCalculationActionClicked(
                    record,
                    ExecutionHistoryReloadActionType.AdvancedAnalysisWizard
                  )
              "
              class="gap-2"
            >
              <FileJson class="size-4" />
              <span>Reload as Advanced Analysis</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </span>
    </a-tooltip>

    <a-tooltip v-if="shouldShowPostProcessingAction" title="Reload Post Processing">
      <span class="inline-block">
        <DropdownMenu>
          <DropdownMenuTrigger as-child>
            <Button
              id="post_processing"
              role="post_processing"
              size="icon"
              variant="secondary"
              :disabled="record?.status !== ExecutionStatusType.Succeeded"
              class="group relative"
            >
              <FileCog class="size-5" />
              <svg
                class="absolute bottom-0.5 right-0.5 size-2 fill-current opacity-50 transition group-hover:opacity-100"
                viewBox="0 0 9 9"
                aria-hidden="true"
              >
                <polygon points="1,1 7,1 4,6" />
              </svg>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" class="min-w-[200px]">
            <!-- Reload -->
            <DropdownMenuItem
              @click="
                () =>
                  onPostProcessingActionClicked(
                    record,
                    ExecutionHistoryReloadActionType.AnalysisWizard
                  )
              "
              class="gap-2"
            >
              <RotateCw class="size-4" />
              <span>Reload Post Processing</span>
            </DropdownMenuItem>

            <DropdownMenuItem
              @click="
                () =>
                  onPostProcessingActionClicked(
                    record,
                    ExecutionHistoryReloadActionType.AdvancedAnalysisWizard
                  )
              "
              class="gap-2"
            >
              <FileJson class="size-4" />
              <span>Reload as Advanced Analysis</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </span>
    </a-tooltip>

    <a-tooltip v-if="shouldShowCustomReportsAction" title="Reload Report">
      <span class="inline-block">
        <DropdownMenu>
          <DropdownMenuTrigger as-child>
            <Button
              id="custom_report"
              role="custom_report"
              size="icon"
              variant="secondary"
              :disabled="record?.status !== ExecutionStatusType.Succeeded"
              class="group relative"
            >
              <BookText class="size-5" />
              <svg
                class="absolute bottom-0.5 right-0.5 size-2 fill-current opacity-50 transition group-hover:opacity-100"
                viewBox="0 0 9 9"
                aria-hidden="true"
              >
                <polygon points="1,1 7,1 4,6" />
              </svg>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" class="min-w-[200px]">
            <!-- Reload -->
            <DropdownMenuItem
              @click="
                () => onCustomReportActionClicked(record, ExecutionHistoryReloadActionType.AnalysisWizard)
              "
              class="gap-2"
            >
              <RotateCw class="size-4" />
              <span>Reload Report</span>
            </DropdownMenuItem>

            <DropdownMenuItem
              @click="
                () =>
                  onCustomReportActionClicked(
                    record,
                    ExecutionHistoryReloadActionType.AdvancedAnalysisWizard
                  )
              "
              class="gap-2"
            >
              <FileJson class="size-4" />
              <span>Reload as Advanced Analysis</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </span>
    </a-tooltip>

    <a-tooltip
      v-if="
        record?.status === ExecutionStatusType.Succeeded ||
        record?.status === ExecutionStatusType.Failed
      "
      title="Download"
    >
      <span class="inline-block">
        <Button
          id="download"
          role="download"
          size="icon"
          variant="secondary"
          @click="() => onDownloadActionClicked(record)"
        >
          <Loader2 v-if="downloadingExecutionId === record.id" class="size-4 animate-spin" />
          <Download v-else class="size-5" />
        </Button>
      </span>
    </a-tooltip>

    <a-tooltip
      v-if="
        showDownloadReport &&
        (record?.status === ExecutionStatusType.Succeeded ||
          record?.status === ExecutionStatusType.Failed)
      "
      title="Download Report"
    >
      <span class="inline-block">
        <Button
          id="download_report"
          role="download_report"
          size="icon"
          variant="secondary"
          @click="() => onDownloadReportActionClicked(record)"
        >
          <Loader2 v-if="downloadingReportExecutionId === record.id" class="size-4 animate-spin" />
          <FileDown v-else class="size-5" />
        </Button>
      </span>
    </a-tooltip>

    <a-tooltip v-if="hasAdminAccess" title="Cancel Execution">
      <span class="inline-block">
        <Button
          id="cancel_execution"
          role="cancel_execution"
          size="icon"
          variant="secondary"
          :disabled="
            record?.status !== ExecutionStatusType.Pending &&
            record?.status !== ExecutionStatusType.Running
          "
          @click="() => onCancelActionClicked(record)"
        >
          <Loader2 v-if="isCancelingExecution" class="size-4 animate-spin" />
          <CircleX v-else class="size-5" />
        </Button>
      </span>
    </a-tooltip>

    <a-tooltip v-if="hasAdminAccess" title="Archive Execution">
      <span class="inline-block">
        <Button
          id="archive_execution"
          role="archive_execution"
          size="icon"
          variant="secondary"
          :disabled="
            record?.status !== ExecutionStatusType.Succeeded &&
            record?.status !== ExecutionStatusType.Failed &&
            record?.status !== ExecutionStatusType.Cancelled
          "
          @click="() => onArchiveActionClicked(record)"
        >
          <Loader2 v-if="isArchivingExecution" class="size-4 animate-spin" />
          <Archive v-else class="size-5" />
        </Button>
      </span>
    </a-tooltip>

    <a-tooltip v-if="props.hasAdminAccess" title="Move to Folder">
      <span class="inline-block">
        <Button
          id="move_to_folder"
          role="move_to_folder"
          size="icon"
          variant="secondary"
          @click="() => onMoveToFolderClicked(record)"
        >
          <FolderOutput class="size-5" />
        </Button>
      </span>
    </a-tooltip>
  </div>
</template>

<style scoped></style>
